// portal/src/lib/agentApi.ts
// ────────────────────────────────────────────────────────────────────
// Typed client for POST /api/agent/chat. Reuses the shared axios `api`
// instance, so Authorization + X-Tenant-Id headers and 401/403 handling
// come for free. The server is stateless — we send the recent transcript
// each call (multi-turn lives on the client).
// ────────────────────────────────────────────────────────────────────
import { api } from "./api";

export type FocusType = "plant" | "area" | "asset" | "cml" | null;

export interface Focus {
  type: FocusType;
  ref: string;
  label: string;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

/** Deterministic metrics — the source of truth. A dict for one agent; an array
 *  of {agent, metrics} when MODS composes >=2 specialists (multi-card UI is a
 *  later slice); null = no agent handled it. The dock reads known keys. */
export type ChatMetrics =
  | Record<string, unknown>
  | Array<{ agent: string; metrics: Record<string, unknown> | null }>
  | null;

export interface ChatResponse {
  metrics: ChatMetrics;
  answer: string;
  provenance: Record<string, unknown>;
}

/** Send a question with the recent transcript + workspace focus. `tool`, when
 *  set, names a retrieval tool for the server to dispatch directly (starter
 *  chips) — no LLM involved; free-text calls omit it. */
export async function agentChat(
  question: string,
  messages: ChatMessage[],
  focus: Focus | null,
  tool?: string,
): Promise<ChatResponse> {
  const { data } = await api.post<ChatResponse>("/api/agent/chat", {
    question,
    messages,
    focus,
    tool,
  });
  return data;
}
